import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import SEO from "@/components/SEO";
import { MapPin, Star, Check, ArrowUpRight, ArrowLeft, Award } from "lucide-react";

export default function HotelDetail() {
  const { id } = useParams();
  const [hotel, setHotel] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setHotel(null); setNotFound(false);
    api.get(`/hotels/${id}`).then(({ data }) => setHotel(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="container-x py-28 text-center">
      <div className="font-serif text-4xl">We couldn't find that hotel.</div>
      <Link to="/operators" className="btn-ghost mt-6 inline-flex">Back to operators</Link>
    </div>
  );
  if (!hotel) return <div className="container-x py-28 text-center text-charcoal/60">Loading…</div>;

  const sortedDeals = [...(hotel.deals || [])]
    .map((d, originalIdx) => ({ ...d, originalIdx }))
    .sort((a, b) => a.price - b.price);
  const bestPrice = sortedDeals[0]?.price;

  return (
    <div>
      <SEO
        title={`${hotel.name} — ${hotel.destination_name}`}
        description={`${hotel.tagline}. Compare rates across ${hotel.deals?.length || 0} operators starting from $${Math.round(bestPrice || hotel.price_from)}/night.`}
        image={hotel.image}
        type="article"
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 h-[60vh]">
          <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/10 to-sand" />
        </div>
        <div className="relative container-x pt-10 pb-20 md:pt-14 md:pb-28">
          <Link to="/operators" className="inline-flex items-center gap-2 text-sand/90 hover:text-sand text-sm" data-testid="back-to-compare">
            <ArrowLeft size={14} /> Back to operators
          </Link>
          <div className="mt-[32vh] md:mt-[36vh] max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 text-sand">
              <span className="bg-sand/90 text-charcoal text-[10px] uppercase tracking-[0.2em] px-2.5 py-1">{hotel.brand}</span>
              <span className="flex items-center gap-1 text-sm"><MapPin size={14} /> {hotel.destination_name}</span>
              <span className="flex items-center gap-0.5">
                {Array.from({ length: hotel.stars }).map((_, i) => (
                  <Star key={i} size={13} className="fill-gold text-gold" />
                ))}
              </span>
            </div>
            <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-sand">{hotel.name}</h1>
            <p className="mt-4 text-sand/90 text-lg max-w-2xl">{hotel.tagline}</p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="container-x py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7 space-y-10">
          <div>
            <div className="overline text-terracotta">Editor's take</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl leading-snug text-charcoal/90">
              {hotel.description}
            </p>
          </div>

          <div>
            <div className="overline mb-4">What's inside</div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {hotel.amenities.map((a) => (
                <li key={a} className="flex items-center gap-2 text-sm text-charcoal/80">
                  <Check size={14} className="text-terracotta" />{a}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-charcoal/10 bg-white p-7 flex items-center gap-5">
            <div className="w-14 h-14 rounded-full bg-forest flex items-center justify-center text-sand">
              <Award size={22} />
            </div>
            <div>
              <div className="overline">Editor score</div>
              <div className="font-serif text-3xl leading-none mt-1">
                {hotel.editor_score?.toFixed(1)} <span className="text-lg text-charcoal/50">/ 10</span>
              </div>
              <div className="text-sm text-charcoal/70 mt-1">
                Tested against 27 other properties in {hotel.destination_name}.
              </div>
            </div>
          </div>
        </div>

        {/* DEAL PANEL */}
        <aside className="md:col-span-5">
          <div className="sticky top-28 border border-charcoal/10 bg-white" data-testid="deals-panel">
            <div className="p-7 border-b border-charcoal/10">
              <div className="overline">Live rates from</div>
              <div className="font-serif text-3xl mt-1">{sortedDeals.length} operators</div>
              {bestPrice && (
                <div className="mt-3 text-sm text-charcoal/70">
                  Best tonight: <span className="font-mono text-terracotta font-semibold">${Math.round(bestPrice)}</span>
                </div>
              )}
            </div>
            <ul>
              {sortedDeals.map((d, i) => (
                <li key={i} className="flex items-center justify-between px-7 py-5 border-b border-charcoal/5 last:border-b-0">
                  <div>
                    <div className="font-medium">{d.operator}</div>
                    {d.note && <div className="text-xs text-charcoal/55 mt-0.5">{d.note}</div>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono text-lg">${Math.round(d.price)}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-charcoal/40">per night</div>
                    </div>
                    <a
                      href={`${API}/go/${hotel.id}/${d.originalIdx}`}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`deal-${i}-cta`}
                      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                        i === 0 ? "bg-terracotta border-terracotta text-sand hover:bg-terracotta-light" : "border-charcoal/20 hover:border-charcoal"
                      }`}
                    >
                      View <ArrowUpRight size={12} />
                    </a>
                  </div>
                </li>
              ))}
            </ul>
            <div className="p-5 bg-cream/60 text-[11px] text-charcoal/55 leading-relaxed">
              Rates shown are before taxes & resort fees. Availability changes live; we don't process payments.
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
